import { GoogleDuotoneIcon } from '@blocksuite/icons/rc';
import { notify } from '@pique/component';
import { useAsyncCallback } from '@pique/core/hooks/pique-async-hooks';
import { posthog } from '@pique/core/posthog';
import { OAuthProviderType } from '@pique/graphql';
import { useService } from '@toeverything/infra';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

import { AuthService } from '../../../modules/cloud';

const buttonStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  padding: '12px 24px',
  borderRadius: '12px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: 'var(--affine-background-overlay-panel-color)',
  border: '1px solid var(--affine-border-color)',
  boxShadow: 'var(--affine-shadow-1)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  isolation: 'isolate',
} as const;

const iconWrapperStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: 'var(--affine-white)',
  boxShadow: 'var(--affine-shadow-1)',
} as const;

const textStyles = {
  fontSize: 'var(--affine-font-base)',
  fontWeight: '600',
  color: 'var(--affine-text-primary-color)',
  flex: 1,
  textAlign: 'center',
  marginRight: '24px',
} as const;

const AppleLogoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 384 512" fill="#000" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const OAuthProviderMap: Partial<
  Record<OAuthProviderType, { icon: ReactElement }>
> = {
  [OAuthProviderType.Google]: {
    icon: <GoogleDuotoneIcon />,
  },
  [OAuthProviderType.Apple]: {
    icon: <AppleLogoIcon />,
  },
};

export function OAuth({
  redirectUri,
  buttonText,
  forceConsent,
}: {
  redirectUri?: string | null;
  buttonText?: string;
  forceConsent?: boolean;
}) {
  return (
    <>
      <OAuthProvider
        provider={OAuthProviderType.Google}
        redirectUri={redirectUri}
        buttonText={buttonText}
        forceConsent={forceConsent}
      />
      {environment.isTauri && (
        <OAuthProvider
          provider={OAuthProviderType.Apple}
          redirectUri={redirectUri}
        />
      )}
    </>
  );
}

function OAuthProvider({
  provider,
  redirectUri,
  buttonText,
  forceConsent,
}: {
  provider: OAuthProviderType;
  redirectUri?: string | null;
  buttonText?: string;
  forceConsent?: boolean;
}) {
  const { icon } = OAuthProviderMap[provider] as { icon: ReactElement };
  const authService = useService(AuthService);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    if (
      !isConnecting ||
      !environment.isTauri ||
      provider !== OAuthProviderType.Google
    ) {
      setShowFallback(false);
      return;
    }
    const timer = setTimeout(() => setShowFallback(true), 8000);
    return () => clearTimeout(timer);
  }, [isConnecting, provider]);

  const onClick = useAsyncCallback(async () => {
    const platform = environment.isTauri ? 'desktop' : 'web';
    try {
      setIsConnecting(true);
      posthog.track('sign_in_started', {
        provider,
        platform,
      });
      await authService.signInOauth(provider, redirectUri, {
        forceConsent,
      });
    } catch (err) {
      console.error('OAuth sign-in failed:', err);
      posthog.track('sign_in_failed', {
        provider,
        platform,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      notify.error({ title: 'Failed to sign in, please try again.' });
    } finally {
      setIsConnecting(false);
    }
  }, [authService, forceConsent, provider, redirectUri]);

  const onFallbackClick = useAsyncCallback(async () => {
    setIsConnecting(false);
    setShowFallback(false);
    authService.signInOauthInApp(provider, { forceConsent });
  }, [authService, forceConsent, provider]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <button
        onClick={onClick}
        disabled={isConnecting}
        style={{
          ...buttonStyles,
          marginTop: 30,
          width: '100%',
          cursor: isConnecting ? 'wait' : 'pointer',
          opacity: isConnecting ? 0.8 : 1,
        }}
      >
        <div style={iconWrapperStyles}>{icon}</div>
        <span style={textStyles}>
          {showFallback
            ? 'Waiting for browser...'
            : buttonText
              ? buttonText.replace('{provider}', provider)
              : `Continue with ${provider}`}
        </span>
        {isConnecting && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'var(--affine-background-overlay-panel-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.7,
            }}
          >
            <div className="oauth-spinner" />
          </div>
        )}
      </button>
      {showFallback && (
        <button
          className="oauth-fallback-enter"
          onClick={onFallbackClick}
          style={{
            marginTop: 12,
            background: 'none',
            border: 'none',
            color: 'var(--affine-text-secondary-color)',
            fontSize: 'var(--affine-font-xs)',
            cursor: 'pointer',
            padding: '4px 0',
          }}
        >
          Having trouble signing in?{' '}
          <span style={{ color: 'var(--affine-link-color)' }}>
            Sign in here
          </span>
        </button>
      )}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes oauthFallbackIn {
            from { opacity: 0; transform: translateY(4px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .oauth-spinner {
            width: 20px;
            height: 20px;
            border: 2px solid var(--affine-primary-color);
            border-top-color: transparent;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }
          .oauth-fallback-enter {
            animation: oauthFallbackIn 0.4s ease forwards;
          }
        `}
      </style>
    </div>
  );
}
